/**
 * Mini Cart Component
 *
 * Floating cart widget for header.
 */

import { useState, useEffect, useRef } from 'react';
import { useCart, useShop } from '@/context';
import { Button } from '../shared/Button';
import { Spinner } from '../shared/Spinner';
import { QuantityInput } from '../shared/QuantityInput';
import styles from './MiniCart.module.css';

/**
 * Build checkout URL with session key for guest users.
 * This helps maintain cart state when cookies are blocked (private browsing).
 */
function getCheckoutUrl(baseUrl: string): string {
  const config = window.evcShop;
  if (!config.isLoggedIn && config.sessionKey) {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}evcs_session=${encodeURIComponent(config.sessionKey)}`;
  }
  return baseUrl;
}

interface MiniCartProps {
  showIcon?: boolean;
}

export function MiniCart({ showIcon = true }: MiniCartProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { cart, itemCount, isEmpty, isLoading, updateItem, removeItem, refreshCart } = useCart();
  const { formatPrice, t, urls } = useShop();

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Listen for cart updates from other components
  useEffect(() => {
    const handleCartUpdate = async () => {
      // Refresh cart data from server (separate React roots don't share state)
      await refreshCart();
      // Show cart when item added (no auto-close)
      setIsOpen(true);
    };

    const handleCartSync = () => {
      refreshCart();
    };

    window.addEventListener('evcs:cart:item-added', handleCartUpdate);
    window.addEventListener('evcs:cart:updated', handleCartSync);
    return () => {
      window.removeEventListener('evcs:cart:item-added', handleCartUpdate);
      window.removeEventListener('evcs:cart:updated', handleCartSync);
    };
  }, [refreshCart]);

  const handleQuantityChange = async (itemKey: string, quantity: number) => {
    try {
      if (quantity === 0) {
        await removeItem(itemKey);
      } else {
        await updateItem(itemKey, quantity);
      }
    } catch {
      // Error handled in context
    }
  };

  const handleRemove = async (itemKey: string) => {
    try {
      await removeItem(itemKey);
    } catch {
      // Error handled in context
    }
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <div
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsOpen(!isOpen); }}
        role="button"
        tabIndex={0}
        aria-label={`${t('cart')} (${itemCount})`}
        aria-expanded={isOpen}
      >
        {showIcon && <CartIcon />}
        {itemCount > 0 && <span className={styles.badge}>{itemCount}</span>}
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.header}>
            <h3 className={styles.title}>{t('cart')}</h3>
            <div
              className={styles.close}
              onClick={() => setIsOpen(false)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsOpen(false); }}
              role="button"
              tabIndex={0}
              aria-label={t('close')}
            >
              <CloseIcon />
            </div>
          </div>

          <div className={styles.content}>
            {isLoading && isEmpty ? (
              <div className={styles.loading}>
                <Spinner />
              </div>
            ) : isEmpty ? (
              <p className={styles.empty}>{t('emptyCart')}</p>
            ) : (
              <ul className={styles.items}>
                {cart.items.map((item) => (
                  <li key={item.key} className={styles.item}>
                    <a href={item.permalink} className={styles.itemImageLink}>
                      <img
                        src={item.image}
                        alt={item.name}
                        className={styles.itemImage}
                      />
                    </a>
                    <div className={styles.itemDetails}>
                      <div className={styles.itemInfo}>
                        <a href={item.permalink} className={styles.itemNameLink}>
                          {item.name}
                        </a>
                        {item.variation_attributes &&
                          Object.entries(item.variation_attributes).length > 0 && (
                            <span className={styles.itemVariation}>
                              {Object.values(item.variation_attributes).join(' / ')}
                            </span>
                          )}
                        {item.meta ? (
                          <div className={styles.itemMeta} style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                            {item.meta.options && Array.isArray(item.meta.options) ? (
                              item.meta.options.map((opt: any, idx: number) => (
                                <div key={idx} style={{ marginBottom: idx < item.meta!.options.length - 1 ? '6px' : 0 }}>
                                  <strong>{opt.option_name || 'Personnalisation'}</strong>
                                  <ul style={{ paddingLeft: '0', listStyle: 'none', margin: '2px 0' }}>
                                    {opt.initials && <li>Initiales: {opt.initials}</li>}
                                    {opt.names && <li>Prénoms: {opt.names}</li>}
                                    {opt.date && <li>Date: {opt.date}</li>}
                                    {opt.message && <li>Message: {opt.message}</li>}
                                    {opt.color && <li>Couleur: {opt.color}</li>}
                                    {opt.font && <li>Police: {opt.font}</li>}
                                    {opt.location && <li>Emplacement: {opt.location}</li>}
                                  </ul>
                                </div>
                              ))
                            ) : (
                              <>
                                <strong>{item.meta.option_name || 'Personnalisation'}</strong>
                                <ul style={{ paddingLeft: '0', listStyle: 'none', margin: '2px 0' }}>
                                  {item.meta.initials && <li>Initiales: {item.meta.initials}</li>}
                                  {item.meta.names && <li>Prénoms: {item.meta.names}</li>}
                                  {item.meta.date && <li>Date: {item.meta.date}</li>}
                                  {item.meta.message && <li>Message: {item.meta.message}</li>}
                                  {item.meta.color && <li>Couleur: {item.meta.color}</li>}
                                  {item.meta.font && <li>Police: {item.meta.font}</li>}
                                  {item.meta.location && <li>Emplacement: {item.meta.location}</li>}
                                </ul>
                              </>
                            )}
                          </div>
                        ) : (
                          <span style={{ fontSize: '0.85em', color: '#999', marginTop: '4px', display: 'block' }}>
                            Sans personnalisation
                          </span>
                        )}
                      </div>
                      <QuantityInput
                        value={item.quantity}
                        min={0}
                        max={item.stock ?? undefined}
                        onChange={(qty) => handleQuantityChange(item.key, qty)}
                        size="small"
                      />
                    </div>
                    <div className={styles.itemActions}>
                      <div
                        className={styles.removeButton}
                        onClick={() => handleRemove(item.key)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleRemove(item.key); }}
                        role="button"
                        tabIndex={0}
                        aria-label={t('removeFromCart')}
                      >
                        <TrashIcon />
                      </div>
                      <span className={styles.itemPrice}>
                        {formatPrice(item.price)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {!isEmpty && (
            <div className={styles.footer}>
              <div className={styles.subtotal}>
                <span>{t('subtotal')}</span>
                <span className={styles.subtotalAmount}>
                  {formatPrice(cart.totals.subtotal)}
                </span>
              </div>
              <div className={styles.actions}>
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => (window.location.href = urls.cart)}
                >
                  {t('viewCart')}
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => (window.location.href = getCheckoutUrl(urls.checkout))}
                >
                  {t('checkout')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Icons
function CartIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M1.56641 4C1.56641 3.58579 1.90219 3.25 2.31641 3.25H3.49696C4.61854 3.25 5.56885 4.07602 5.72504 5.18668L5.7862 5.62161H19.7507C21.3714 5.62161 22.4605 7.28344 21.8137 8.76953L19.1464 14.8979C18.789 15.719 17.9788 16.25 17.0833 16.25L7.72179 16.25C6.60021 16.25 5.6499 15.424 5.49371 14.3133L4.23965 5.39556C4.18759 5.02534 3.87082 4.75 3.49696 4.75H2.31641C1.90219 4.75 1.56641 4.41421 1.56641 4ZM5.99714 7.12161L6.9791 14.1044C7.03116 14.4747 7.34793 14.75 7.72179 14.75L17.0833 14.75C17.3818 14.75 17.6519 14.573 17.771 14.2993L20.4383 8.17092C20.6539 7.67556 20.2909 7.12161 19.7507 7.12161H5.99714Z" fill="currentColor" />
      <path d="M6.03418 19.5C6.03418 18.5335 6.81768 17.75 7.78418 17.75C8.75068 17.75 9.53428 18.5335 9.53428 19.5C9.53428 20.4665 8.75078 21.25 7.78428 21.25C6.81778 21.25 6.03418 20.4665 6.03418 19.5Z" fill="currentColor" />
      <path d="M16.3203 17.75C15.3538 17.75 14.5703 18.5335 14.5703 19.5C14.5703 20.4665 15.3538 21.25 16.3203 21.25C17.2868 21.25 18.0704 20.4665 18.0704 19.5C18.0704 18.5335 17.2868 17.75 16.3203 17.75Z" fill="currentColor" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="3,6 5,6 21,6" />
      <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6" />
    </svg>
  );
}
